import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import useClipboard from 'react-use-clipboard';

import { TRANSACTION_ITEM_TYPE } from '../../../../constants/transactions';
import {
  getBasenameFromEntity,
  getProductBasenames,
} from '../../../../util/role-definitions';
import { formatPriceForDisplay } from '../../../../util/format-money';
import { getTransactionItemRefundedTotal } from '../../../../util/transactions';
import { DATE_FORMAT_WITH_TIME } from '../../../../constants/dates';
import Button from '../../../global/Button';

const propTypes = {
  transactionItem: PropTypes.shape({
    id: PropTypes.string,
    transaction: PropTypes.shape({
      id: PropTypes.string,
      uuid: PropTypes.string,
      roes_order_id: PropTypes.string,
      print_partner: PropTypes.shape({
        id: PropTypes.string,
        name: PropTypes.string,
        roes_info: PropTypes.shape({
          lab_id: PropTypes.string,
        }),
      }),
    }),
    total_price: PropTypes.shape({
      value: PropTypes.number,
    }),
    item_type: PropTypes.oneOf(Object.keys(TRANSACTION_ITEM_TYPE)),
    item_album: PropTypes.shape({
      id: PropTypes.string,
      name: PropTypes.string,
    }),
    item_album_folder: PropTypes.shape({
      id: PropTypes.string,
      name: PropTypes.string,
    }),
    vendor_state_tax_price: PropTypes.shape({
      value: PropTypes.number,
    }),
    vendor_local_tax_price: PropTypes.shape({
      value: PropTypes.number,
    }),
    tax_total_price: PropTypes.shape({
      value: PropTypes.number,
    }),
  }),
  basename: PropTypes.oneOf(getProductBasenames()),
};

const PrintOrderServiceTransactionDetails = ({ transactionItem, basename }) => {
  const {
    item_album: album,
    item_album_folder: albumFolder,
    print_order_configuration: printOrderConfiguration,
  } = transactionItem;
  const {
    uuid: orderNumber,
    order_number: orderShorthand,
    roes_order_id: roesOrderNumber,
    print_partner: printPartner,
  } = transactionItem?.transaction ?? {};

  const [orderNumberCopied, setOrderNumberCopied] = useClipboard(orderNumber);
  const [orderShorthandCopied, setOrderShorthandCopied] = useClipboard(
    orderShorthand,
  );
  const [roesOrderNumberCopied, setRoesOrderNumberCopied] = useClipboard(
    roesOrderNumber,
  );

  return (
    <div>
      <table className="table table-striped mb-3">
        <tbody>
          <tr>
            <td>
              {' '}
              Order Number{' '}
              <Button btnStyle="link" onClick={setOrderNumberCopied}>
                {orderNumberCopied ? 'Copied!' : 'Copy'}
              </Button>
            </td>
            <td>{orderNumber}</td>
          </tr>
          {orderShorthand && (
            <tr>
              <td>
                Order Shorthand{' '}
                <Button btnStyle="link" onClick={setOrderShorthandCopied}>
                  {orderShorthandCopied ? 'Copied!' : 'Copy'}
                </Button>
              </td>
              <td>{orderShorthand}</td>
            </tr>
          )}
          {roesOrderNumber && (
            <tr>
              <td>
                ROES Order Number{' '}
                <Button btnStyle="link" onClick={setRoesOrderNumberCopied}>
                  {roesOrderNumberCopied ? 'Copied!' : 'Copy'}
                </Button>
              </td>
              <td>{roesOrderNumber}</td>
            </tr>
          )}{' '}
          {printPartner && (
            <tr>
              <td>Print Partner</td>
              <td>
                {printPartner.name}
                {printPartner?.roes_info?.lab_id
                  ? ` (Lab ID: ${printPartner.roes_info.lab_id}})`
                  : ''}
              </td>
            </tr>
          )}
          <tr>
            <td> Transaction Type </td>
            <td> Print Order Service </td>
          </tr>
          <tr>
            <td> Date </td>
            <td>
              {moment(transactionItem.created_at).format(DATE_FORMAT_WITH_TIME)}
            </td>
          </tr>
          <tr>
            <td> Item Type </td>
            <td>
              {printOrderConfiguration?.print_partner_order_services?.records
                ?.map(
                  ({ print_partner_order_service: service }) =>
                    service?.print_order_service?.name,
                )
                .join(', ')}
            </td>
          </tr>
          <tr>
            {album ? (
              <Fragment>
                <td> Album </td>
                {album?.id ? (
                  <td>
                    <a
                      href={`/app/${
                        getBasenameFromEntity(album) ?? basename
                      }/albums/${album.id}`}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {album.name}
                    </a>
                  </td>
                ) : (
                  <td>{transactionItem?.album_name}</td>
                )}
              </Fragment>
            ) : (
              <Fragment>
                <td> Gallery </td>
                <td>
                  <a
                    href={`/app/${
                      getBasenameFromEntity(albumFolder) ?? basename
                    }/folders/${albumFolder.id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {albumFolder.name}
                  </a>
                </td>
              </Fragment>
            )}
          </tr>
          <tr>
            <td> Amount Paid </td>
            <td>
              {(transactionItem?.total_price?.value ?? 0) >= 0 ? (
                <Fragment>
                  $
                  {formatPriceForDisplay(
                    transactionItem?.total_price?.value ?? 0,
                  )}
                </Fragment>
              ) : (
                'N/A'
              )}
            </td>
          </tr>
          {(transactionItem?.vendor_state_tax_price?.value ?? 0) > 0 && (
            <tr>
              <td> State Tax Collected </td>
              <td>
                $
                {formatPriceForDisplay(
                  transactionItem?.vendor_state_tax_price?.value ?? 0,
                )}
              </td>
            </tr>
          )}
          {(transactionItem?.vendor_local_tax_price?.value ?? 0) > 0 && (
            <tr>
              <td> Local Tax Collected </td>
              <td>
                $
                {formatPriceForDisplay(
                  transactionItem?.vendor_local_tax_price?.value ?? 0,
                )}
              </td>
            </tr>
          )}
          {(transactionItem?.tax_total_price?.value ?? 0) > 0 && (
            <tr>
              <td> Total Tax Collected </td>
              <td>
                $
                {formatPriceForDisplay(
                  transactionItem?.tax_total_price?.value ?? 0,
                )}
              </td>
            </tr>
          )}
          <tr>
            <td> Amount Refunded </td>
            <td>
              $
              {formatPriceForDisplay(
                getTransactionItemRefundedTotal(transactionItem),
              )}
            </td>
          </tr>
          <tr>
            <td>Final Amount</td>
            <td>
              {(transactionItem?.total_price?.value ?? 0) >= 0 ? (
                <Fragment>
                  $
                  {formatPriceForDisplay(
                    (transactionItem?.total_price?.value ?? 0) -
                      getTransactionItemRefundedTotal(transactionItem),
                  )}
                </Fragment>
              ) : (
                'N/A'
              )}
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  );
};

PrintOrderServiceTransactionDetails.propTypes = propTypes;

export default PrintOrderServiceTransactionDetails;
